/**
 * Canonical data for Kurs 4's original animated day sky.
 *
 * Motion Canvas and SlideMark both consume this module. Do not create a
 * second approximation in either renderer: changes here alter the original
 * presentation as well as the translated background.
 */
export interface DayStage {
  sky: readonly [string, string, string, string];
  stops: readonly [number, number, number, number];
  ground: string;
  detail: string;
  sun: {x: number; y: number; fill: string; glow: string; blur: number};
  stars: number;
  clouds: number;
  cloudFill: string;
  cloudShade: string;
  dark: boolean;
}

export const DUSK_STOPS = [0, 0.45, 0.75, 0.95] as const;
const DAY_STOPS = [0, 0.52, 0.88, 0.97] as const;
const DAY_CLOUD = '#f2eee0';
const DAY_CLOUD_SHADE = '#9db1cf';
const DUSK_CLOUD = '#e8e0ee';
const DUSK_CLOUD_SHADE = '#4a3c62';

export const DAY: DayStage[] = [
  {sky: ['#241a3f', '#6d3a56', '#c96a4a', '#f4a95c'], stops: DUSK_STOPS, ground: '#14101d', detail: '#0d0a15',
   sun: {x: -420, y: 312, fill: '#ffcf7d', glow: '#ff9a45', blur: 130}, stars: 0.5, clouds: 0,
   cloudFill: DUSK_CLOUD, cloudShade: DUSK_CLOUD_SHADE, dark: true},
  {sky: ['#4b4e8c', '#a86390', '#e8905e', '#ffd08a'], stops: DUSK_STOPS, ground: '#241d2e', detail: '#171221',
   sun: {x: -380, y: 238, fill: '#ffd89a', glow: '#ffb066', blur: 115}, stars: 0, clouds: 0.2,
   cloudFill: DUSK_CLOUD, cloudShade: DUSK_CLOUD_SHADE, dark: true},
  {sky: ['#2b4d7e', '#54799f', '#e8b285', '#ffd9a0'], stops: [0, 0.5, 0.85, 0.97], ground: '#2f3a2e', detail: '#1d251d',
   sun: {x: -760, y: 250, fill: '#ffe2a8', glow: '#ffc27a', blur: 110}, stars: 0, clouds: 0.5,
   cloudFill: DAY_CLOUD, cloudShade: DAY_CLOUD_SHADE, dark: true},
  {sky: ['#1f5286', '#3c74ab', '#7fb0d4', '#ffe6b8'], stops: DAY_STOPS, ground: '#46613f', detail: '#2b3d2a',
   sun: {x: -60, y: -430, fill: '#fff0c2', glow: '#ffd98e', blur: 95}, stars: 0, clouds: 0.85,
   cloudFill: DAY_CLOUD, cloudShade: DAY_CLOUD_SHADE, dark: true},
  {sky: ['#1a5795', '#3f7fbc', '#8cc0e2', '#f6e9c4'], stops: DAY_STOPS, ground: '#4d6f4a', detail: '#2f4630',
   sun: {x: 10, y: -470, fill: '#fff4cd', glow: '#ffe1a0', blur: 90}, stars: 0, clouds: 0.9,
   cloudFill: DAY_CLOUD, cloudShade: DAY_CLOUD_SHADE, dark: true},
  {sky: ['#20518a', '#4a7cae', '#d8c9a4', '#ffd89a'], stops: [0, 0.52, 0.86, 0.97], ground: '#46613f', detail: '#2b3d2a',
   sun: {x: 420, y: -400, fill: '#ffefc0', glow: '#ffd28a', blur: 95}, stars: 0, clouds: 0.8,
   cloudFill: DAY_CLOUD, cloudShade: DAY_CLOUD_SHADE, dark: true},
  {sky: ['#2c3e74', '#5f5f94', '#e09a6a', '#ffbc7e'], stops: [0, 0.5, 0.82, 0.96], ground: '#242c22', detail: '#141a14',
   sun: {x: 640, y: 100, fill: '#ffdf9e', glow: '#ffb066', blur: 105}, stars: 0, clouds: 0.55,
   cloudFill: DUSK_CLOUD, cloudShade: DUSK_CLOUD_SHADE, dark: true},
  {sky: ['#2c2650', '#4a3a6b', '#8a5570', '#c97a58'], stops: DUSK_STOPS, ground: '#1a1426', detail: '#100c1a',
   sun: {x: 460, y: 340, fill: '#ffc97e', glow: '#ff9a55', blur: 100}, stars: 0.35, clouds: 0.2,
   cloudFill: DUSK_CLOUD, cloudShade: DUSK_CLOUD_SHADE, dark: true},
  {sky: ['#150e31', '#45204f', '#a5432f', '#f0994a'], stops: DUSK_STOPS, ground: '#0d0913', detail: '#080610',
   sun: {x: 480, y: 330, fill: '#ffc97e', glow: '#ff9a45', blur: 110}, stars: 0.6, clouds: 0,
   cloudFill: DUSK_CLOUD, cloudShade: DUSK_CLOUD_SHADE, dark: true},
];

export const DAY_STARS: readonly (readonly [number, number, number])[] = [
  [-870, -480, 4], [-690, -350, 3], [-520, -500, 5], [-350, -410, 3],
  [-180, -520, 4], [-40, -380, 3], [130, -470, 5], [300, -350, 3],
  [470, -510, 4], [620, -420, 3], [780, -490, 5], [900, -360, 3],
];

export const DAY_CLOUDS = [
  {x0: -650, y: -460, scale: 0.85, speed: 24, opacity: 0.55},
  {x0: 60, y: -370, scale: 0.55, speed: 40, opacity: 0.45},
  {x0: 560, y: -480, scale: 0.7, speed: 30, opacity: 0.5},
  {x0: 900, y: 170, scale: 0.4, speed: 52, opacity: 0.35},
] as const;

export const DAY_HORIZON = 315;

export const wrapDayCloudDrift = (x0: number, speed: number, time: number) =>
  ((((x0 + 1200 + time * speed) % 2400) + 2400) % 2400) - 1200;
